import { z, ZodError } from 'zod'

// Base schemas for common types
const CoordinateSchema = z.tuple([z.number(), z.number()])
const CoordinatesArraySchema = z.array(CoordinateSchema)
const TimestampSchema = z.union([z.date(), z.string(), z.any()]) // z.any() to allow serverTimestamp()

// Comment schemas
export const CommentCategorySchema = z.enum([
  'hazard',
  'info',
  'tip',
  'warning',
  'closure',
  'condition',
  'general'
])

export const CommentTypeSchema = z.enum(['inline', 'pinned'])

export const CommentSchema = z.object({
  id: z.string().optional(),
  routeId: z.string().min(1).max(100),
  segmentIndex: z.number().min(0).optional(),
  coordinateIndex: z.number().min(0).optional(),
  coordinates: z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180)
  }).optional(),
  text: z.string().min(1).max(1000), // 1000 char limit
  type: CommentTypeSchema,
  category: CommentCategorySchema,
  resolved: z.boolean().optional(),
  resolvedAt: TimestampSchema.optional(),
  resolvedBy: z.string().optional(),
  replies: z.array(z.any()).optional(), // Will validate replies separately
  ownerId: z.string().min(1).max(100),
  ownerDisplayName: z.string().min(1).max(100),
  ownerPhoto: z.string().url().max(500).optional().nullable(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional()
})

// Route schemas
export const RouteTypeSchema = z.enum([
  'single',
  'bikepacking',
  'event',
  'permanent'
])

export const RouteStatusSchema = z.enum(['editing', 'saved', 'submitted', 'published', 'rejected', 'archived'])

export const POICategorySchema = z.enum([
  'camping',
  'water',
  'food',
  'lodging',
  'bike-shop',
  'attraction',
  'viewpoint',
  'restroom',
  'shelter',
  'parking',
  'transport',
  'danger',
  'information'
])

export const POISchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1).max(200),
  description: z.string().max(500).optional(),
  category: POICategorySchema,
  coordinates: z.union([
    CoordinateSchema, // Tuple format [lng, lat]
    z.object({ lat: z.number(), lng: z.number() }) // Object format
  ]),
  routeId: z.string().optional(),
  segmentIndex: z.number().min(0).optional(),
  distanceFromStart: z.number().min(0).optional(),
  photos: z.array(z.string().url().max(500)).max(5).optional(),
  website: z.string().url().max(500).optional(),
  phone: z.string().max(50).optional(),
  address: z.string().max(300).optional(),
  openingHours: z.string().max(200).optional(),
  amenities: z.array(z.string().max(50)).max(20).optional(),
  isOffRoute: z.boolean().optional(),
  addedBy: z.string().optional(),
  addedAt: TimestampSchema.optional()
})

// Route metadata schema (without the large coordinate arrays)
export const RouteMetadataSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1).max(200),
  description: z.string().max(5000).optional(),
  routeType: RouteTypeSchema,
  status: RouteStatusSchema.optional(),
  type: z.enum(['draft', 'published', 'autosave']).optional(),
  location: z.object({
    city: z.string().max(100),
    state: z.string().max(100),
    country: z.string().max(100),
    region: z.string().max(100)
  }).optional(),
  region: z.string().max(100).optional(),
  country: z.string().max(100).optional(),
  distance: z.number().min(0).max(50000), // Max 50,000km
  elevationGain: z.number().min(0).max(100000), // Max 100,000m
  elevationLoss: z.number().min(0).optional(),
  difficulty: z.enum(['easy', 'moderate', 'hard', 'expert']).optional(),
  surface: z.string().max(100).optional(),
  tags: z.array(z.string().max(50)).max(20).optional(),
  highlights: z.array(z.string().max(200)).max(10).optional(),
  warnings: z.array(z.string().max(300)).max(10).optional(),
  tips: z.array(z.string().max(300)).max(10).optional(),
  isLoop: z.boolean().optional(),
  thumbnail: z.string().url().max(500).optional().nullable(),
  images: z.array(z.string().url().max(500)).max(20).optional(),
  gpxUrl: z.string().url().max(500).optional(),
  ownerId: z.string().min(1).max(100),
  ownerDisplayName: z.string().min(1).max(100),
  ownerPhoto: z.string().url().max(500).optional().nullable(),
  isPublic: z.boolean().optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional(),
  publishedAt: TimestampSchema.optional()
})

// Segment schema (for multi-segment routes)
export const SegmentSchema = z.object({
  id: z.string().optional(),
  name: z.string().max(200).optional(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  distance: z.number().min(0).max(10000), // Max 10,000km per segment
  elevationGain: z.number().min(0).max(50000),
  duration: z.number().min(0).optional(),
  difficulty: z.enum(['easy', 'moderate', 'hard', 'expert']).optional(),
  surface: z.string().max(100).optional(),
  notes: z.string().max(1000).optional(),
  coordinatesCount: z.number().min(2).max(100000) // Validate coordinate count, not the array itself
})

// Club schema
export const ClubTypeSchema = z.enum([
  'cycling',
  'touring',
  'racing',
  'mountain',
  'gravel',
  'social',
  'advocacy',
  'shop',
  'other'
])

export const ClubSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  location: z.string().max(200),
  coordinates: z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180)
  }),
  clubTypes: z.array(ClubTypeSchema).min(1).max(5),
  website: z.string().url().max(500).optional(),
  email: z.string().email().max(200).optional(),
  phone: z.string().max(50).optional(),
  socialMedia: z.object({
    facebook: z.string().url().max(300).optional(),
    instagram: z.string().max(100).optional(),
    twitter: z.string().max(100).optional(),
    strava: z.string().url().max(300).optional()
  }).optional(),
  logoURL: z.string().url().max(500).optional(),
  coverPhoto: z.string().url().max(500).optional(),
  memberCount: z.number().min(0).max(100000).optional(),
  routeCount: z.number().min(0).max(10000).optional(),
  isVerified: z.boolean().optional(),
  foundedYear: z.number().min(1800).max(new Date().getFullYear()).optional(),
  contactName: z.string().max(100).optional(),
  rideSchedule: z.string().max(500).optional(),
  joinRequirements: z.string().max(500).optional(),
  ownerId: z.string().min(1).max(100),
  ownerDisplayName: z.string().min(1).max(100),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional()
})

// User profile schema
export const UserProfileSchema = z.object({
  id: z.string().optional(),
  displayName: z.string().min(1).max(100),
  email: z.string().email().max(200),
  photoURL: z.string().url().max(500).optional().nullable(),
  bio: z.string().max(500).optional(),
  location: z.string().max(200).optional(),
  website: z.string().url().max(300).optional(),
  socialLinks: z.object({
    strava: z.string().url().max(300).optional(),
    instagram: z.string().max(100).optional(),
    twitter: z.string().max(100).optional()
  }).optional(),
  preferences: z.object({
    units: z.enum(['metric', 'imperial']).optional(),
    mapStyle: z.string().max(50).optional(),
    defaultRouteType: RouteTypeSchema.optional(),
    emailNotifications: z.boolean().optional()
  }).optional(),
  stats: z.object({
    routesCreated: z.number().min(0).optional(),
    totalDistance: z.number().min(0).optional(),
    totalElevation: z.number().min(0).optional()
  }).optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional()
})

// Review/Rating schema
export const ReviewSchema = z.object({
  id: z.string().optional(),
  routeId: z.string().min(1).max(100),
  rating: z.number().min(1).max(5),
  title: z.string().min(1).max(200).optional(),
  text: z.string().min(10).max(2000),
  pros: z.array(z.string().max(200)).max(5).optional(),
  cons: z.array(z.string().max(200)).max(5).optional(),
  conditions: z.string().max(500).optional(),
  dateRidden: z.string().optional(),
  photos: z.array(z.string().url().max(500)).max(5).optional(),
  helpful: z.number().min(0).optional(),
  unhelpful: z.number().min(0).optional(),
  verified: z.boolean().optional(),
  ownerId: z.string().min(1).max(100),
  ownerDisplayName: z.string().min(1).max(100),
  ownerPhoto: z.string().url().max(500).optional().nullable(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema.optional()
})

// Helper function to validate with better error messages
export function validateWithDetails<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  context?: string
): T {
  try {
    return schema.parse(data)
  } catch (error) {
    if (error instanceof ZodError) {
      const fieldErrors = error.issues?.map(issue => 
        `${issue.path.join('.')}: ${issue.message}`
      ).join(', ') || 'Unknown validation error'
      throw new Error(
        `Validation failed${context ? ` for ${context}` : ''}: ${fieldErrors}`
      )
    }
    throw error
  }
}

// Helper to sanitize strings (remove control characters, trim whitespace)
export function sanitizeString(str: string | undefined | null, maxLength: number): string {
  // Handle non-string inputs
  if (str === undefined || str === null) {
    return ''
  }
  if (typeof str !== 'string') {
    return String(str)
  }
  return str
    .replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Remove control characters
    .trim()
    .slice(0, maxLength)
}

// Helper to validate partial updates (makes all fields optional)
export function createUpdateSchema<T extends z.ZodObject<any>>(schema: T) {
  return schema.partial()
}